import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import './App.css';

// Context
import { AuthProvider } from './context/AuthContext';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import { UpgradeRequiredDialogHost } from './components/UpgradeRequiredDialog';
import { AppShell } from './components/Layout';

// Pages
import LandingPage from './pages/LandingPage';
import SecurityDashboardPage from './pages/security/SecurityDashboardPage';
import GateAttendancePage from './pages/security/GateAttendancePage';
import ParentQRPage from './pages/security/ParentQRPage';
import DriverTrackingPage from './pages/security/DriverTrackingPage';
import QRVerificationPage from './pages/security/QRVerificationPage';
import AdminTrackingPage from './pages/security/AdminTrackingPage';
import ParentSecurityPortalPage from './pages/security/ParentSecurityPortalPage';
import CollectorSharePage from './pages/security/CollectorSharePage';
import AboutPage from './pages/AboutPage';
import FeaturesPage from './pages/FeaturesPage';
import ContactPage from './pages/ContactPage';
import DocumentationPage from './pages/DocumentationPage';
import PricingPage from './pages/PricingPage';
import LoginPage from './pages/LoginPage';
import KioskLoginPage from './pages/KioskLoginPage';
import KioskPortalPage from './pages/KioskPortalPage';
import ApplyPage from './pages/public/ApplyPage';
import DepositPaymentPage from './pages/public/DepositPaymentPage';
import DashboardPage from './pages/DashboardPage';
import StudentsPage from './pages/StudentsPage';
import StudentDetailPage from './pages/StudentDetailPage';
import StaffPage from './pages/StaffPage';
import StaffDetailPage from './pages/StaffDetailPage';
import ClassesPage from './pages/ClassesPage';
import ClassDetailPage from './pages/ClassDetailPage';
import AttendancePage from './pages/AttendancePage';
import GradesPage from './pages/GradesPage';
import ReportTemplatesPage from './pages/ReportTemplatesPage';
import FeesPage from './pages/FeesPage';
import PayrollPage from './pages/PayrollPage';
import StaffAttendancePage from './pages/StaffAttendancePage';
import StaffClockInPage from './pages/StaffClockInPage';
import LeaveRequestsPage from './pages/LeaveRequestsPage';
import TimetablePage from './pages/TimetablePage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import TicketsPage from './pages/TicketsPage';
import FaqPage from './pages/FaqPage';
import ParentRequestsAdminPage from './pages/ParentRequestsAdminPage';
import SettingsPage from './pages/SettingsPage';
import RolesPermissionsPage from './pages/RolesPermissionsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import ProcurementPage from './pages/inventory/ProcurementPage';
import HRPerformancePage from './pages/HRPerformancePage';
import HRRecruitmentPage from './pages/HRRecruitmentPage';
import HRAdministrationPage from './pages/HRAdministrationPage';
import HROvertimePage from './pages/HROvertimePage';
import CurriculumPlanningPage from './pages/CurriculumPlanningPage';
import TracksPage from './pages/TracksPage';
import SensitiveSupportPage from './pages/SensitiveSupportPage';
import StudentSupportPage from './pages/StudentSupportPage';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import StrategicReportsPage from './pages/StrategicReportsPage';
import ExecutiveDashboardPage from './pages/ExecutiveDashboardPage';
import ExecutiveInsightsPage from './pages/ExecutiveInsightsPage';
import BudgetPage from './pages/BudgetPage';
import OperationalInsightsPage from './pages/OperationalInsightsPage';
import StrategicPlanningPage from './pages/StrategicPlanningPage';
import AcademicCalendarPage from './pages/AcademicCalendarPage';
import FinancePage from './pages/FinancePage';
import BillingPage from './pages/BillingPage';
import BillingDocsDashboard from './pages/BillingDocsDashboard';
import BillingAdminPage from './pages/BillingAdminPage';
import TransportDashboardPage from './pages/transport/TransportDashboardPage';
import VehiclesManagementPage from './pages/transport/VehiclesManagementPage';
import RoutesManagementPage from './pages/transport/RoutesManagementPage';
import StudentEnrollmentPage from './pages/transport/StudentEnrollmentPage';
import AttendanceTrackingPage from './pages/transport/AttendanceTrackingPage';
import TransportFeesPage from './pages/transport/TransportFeesPage';
import MaintenanceLogsPage from './pages/transport/MaintenanceLogsPage';
import DriversManagementPage from './pages/transport/DriversManagementPage';
// Hostel Module Pages
import HostelDashboardPage from './pages/hostel/HostelDashboardPage';
import HostelsManagementPage from './pages/hostel/HostelsManagementPage';
import RoomsManagementPage from './pages/hostel/RoomsManagementPage';
import StudentAllocationsPage from './pages/hostel/StudentAllocationsPage';
import HostelFeesPage from './pages/hostel/HostelFeesPage';
import HostelAttendancePage from './pages/hostel/HostelAttendancePage';
import MaintenanceLogsPageHostel from './pages/hostel/MaintenanceLogsPage';
import ComplaintsPage from './pages/hostel/ComplaintsPage';
import VisitorLogsPage from './pages/hostel/VisitorLogsPage';
// Front Office Module Pages
import VisitorLogPage from './pages/front-office/VisitorLogPage';
// Admissions Module Pages
import AdmissionsApplicantsPage from './pages/admissions/AdmissionsApplicantsPage';
// Health Module Pages
import HealthProfilesPage from './pages/health/HealthProfilesPage';
import ClinicVisitsPage from './pages/health/ClinicVisitsPage';
import ImmunizationsPage from './pages/health/ImmunizationsPage';
import HealthIncidentsPage from './pages/health/HealthIncidentsPage';
import ScreeningCampaignsPage from './pages/health/ScreeningCampaignsPage';
// Discipline Module Pages
import IncidentReportsPage from './pages/discipline/IncidentReportsPage';
// Inventory Module Pages
import AssetCategoriesPage from './pages/inventory/AssetCategoriesPage';
import AssetsRegisterPage from './pages/inventory/AssetsRegisterPage';
import StockItemsPage from './pages/inventory/StockItemsPage';
// Alumni Module Pages
import AlumniRecordsPage from './pages/alumni/AlumniRecordsPage';
// Exam Board Module Pages
import ExamBoardRegistrationsPage from './pages/exam-board/ExamBoardRegistrationsPage';
// Certificates / ID Cards Module Pages
import CertificateIssuancePage from './pages/certificates/CertificateIssuancePage';
import CertificateTemplatesPage from './pages/certificates/CertificateTemplatesPage';
import IDCardsPage from './pages/certificates/IDCardsPage';
// Campus Module Pages
import CampusesPage from './pages/campuses/CampusesPage';
import ExamSchedulingPage from './pages/exams/ExamSchedulingPage';
import ExamOfficePage from './pages/ExamOfficePage';
import ReportsPage from './pages/ReportsPage';
import ParentPortalPage from './pages/ParentPortalPage';
import StudentPortalPage from './pages/StudentPortalPage';
import TeacherPortalPage from './pages/TeacherPortalPage';
import SuperAdminPortalPage from './pages/SuperAdminPortalPage';
import HRPortalPage from './pages/HRPortalPage';
import NursePortalPage from './pages/NursePortalPage';
import RegistrarPortalPage from './pages/RegistrarPortalPage';
import StorekeeperPortalPage from './pages/StorekeeperPortalPage';
import SMSDashboardPage from './pages/communication/SMSDashboardPage';
import CanteenAdminPage from './pages/CanteenAdminPage';
import CanteenPage from './pages/CanteenPage';
import ExtraClassesAdminPage from './pages/ExtraClassesAdminPage';
import ForcePasswordChangePage from './pages/ForcePasswordChangePage';
import LibraryAdminPage from './pages/library/LibraryAdminPage';
import OperationsPage from './pages/OperationsPage';
import SurveyResponsePage from './pages/SurveyResponsePage';
import SurveyBuilderPage from './pages/SurveyBuilderPage';

function App() {
  useEffect(() => {
    const onUpdateAvailable = () => {
      toast.info('A new version of Campusio is available.', {
        action: { label: 'Reload', onClick: () => window.location.reload() },
        duration: Infinity,
      });
    };
    window.addEventListener('campusio:sw-update-available', onUpdateAvailable);
    return () => window.removeEventListener('campusio:sw-update-available', onUpdateAvailable);
  }, []);

  return (
    <AuthProvider>
      <Router>
        <Toaster position="top-right" richColors />
        <UpgradeRequiredDialogHost />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/documentation" element={<DocumentationPage />} />
          <Route path="/login" element={<LoginPage />} />
          {/* Kiosk login — shared-device student portal access, its own
              opaque session token (see utils/kioskSession.js), no user
              login needed. Must stay OUTSIDE the ProtectedRoute-wrapped
              block below: that wrapper redirects to /login for anyone
              without a real session, which would make this unreachable. */}
          <Route path="/kiosk" element={<KioskLoginPage />} />
          <Route path="/kiosk/portal" element={<KioskPortalPage />} />
          <Route path="/apply/:schoolCode" element={<ApplyPage />} />
          <Route path="/apply/:schoolCode/deposit/:depositId" element={<DepositPaymentPage />} />

          {/* Force password change — protected but no role gate */}
          <Route path="/change-password" element={
            <ProtectedRoute>
              <ForcePasswordChangePage />
            </ProtectedRoute>
          } />

          {/* Persistent shell — sidebar stays mounted across all page navigations */}
          <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>

          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          
          <Route path="/students" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'teacher']}>
              <StudentsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/students/:studentId" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'teacher']}>
              <StudentDetailPage />
            </ProtectedRoute>
          } />
          
          <Route path="/staff" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <StaffPage />
            </ProtectedRoute>
          } />
          
          <Route path="/staff/:staffId" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <StaffDetailPage />
            </ProtectedRoute>
          } />
          
          <Route path="/classes" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'teacher']}>
              <ClassesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/classes/:classId" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'teacher']}>
              <ClassDetailPage />
            </ProtectedRoute>
          } />
          
          <Route path="/attendance" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'teacher']}>
              <AttendancePage />
            </ProtectedRoute>
          } />
          
          <Route path="/grades" element={
            <ProtectedRoute>
              <GradesPage />
            </ProtectedRoute>
          } />

          <Route path="/grades/report-templates" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <ReportTemplatesPage />
            </ProtectedRoute>
          } />

          <Route path="/fees" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'parent']}>
              <FeesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/payroll" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr']} module="payroll">
              <PayrollPage />
            </ProtectedRoute>
          } />

          <Route path="/staff-attendance" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr']} module="staff_attendance">
              <StaffAttendancePage />
            </ProtectedRoute>
          } />

          <Route path="/staff-clock-in" element={
            <ProtectedRoute>
              <StaffClockInPage />
            </ProtectedRoute>
          } />

          <Route path="/leave-requests" element={
            <ProtectedRoute>
              <LeaveRequestsPage />
            </ProtectedRoute>
          } />

          <Route path="/finance" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr']}>
              <FinancePage />
            </ProtectedRoute>
          } />
          
          <Route path="/billing" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <BillingPage />
            </ProtectedRoute>
          } />
          
          <Route path="/billing-docs" element={<BillingDocsDashboard />} />

          <Route path="/admin/billing" element={
            <ProtectedRoute roles={['super_admin']}>
              <BillingAdminPage />
            </ProtectedRoute>
          } />

          {/* Transport Module Routes */}
          <Route path="/transport" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']} module="transport_module">
              <TransportDashboardPage />
            </ProtectedRoute>
          } />

          <Route path="/transport/vehicles" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']} module="transport_module">
              <VehiclesManagementPage />
            </ProtectedRoute>
          } />

          <Route path="/transport/routes" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']} module="transport_module">
              <RoutesManagementPage />
            </ProtectedRoute>
          } />

          <Route path="/transport/enrollment" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']} module="transport_module">
              <StudentEnrollmentPage />
            </ProtectedRoute>
          } />

          <Route path="/transport/attendance" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']} module="transport_module">
              <AttendanceTrackingPage />
            </ProtectedRoute>
          } />

          <Route path="/transport/fees" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']} module="transport_module">
              <TransportFeesPage />
            </ProtectedRoute>
          } />

          <Route path="/transport/maintenance" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']} module="transport_module">
              <MaintenanceLogsPage />
            </ProtectedRoute>
          } />

          <Route path="/transport/drivers" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']} module="transport_module">
              <DriversManagementPage />
            </ProtectedRoute>
          } />
          
          {/* End Transport Module Routes */}
          
          {/* Hostel Module Routes */}
          <Route path="/hostel" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <HostelDashboardPage />
            </ProtectedRoute>
          } />
          
          <Route path="/hostel/hostels" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <HostelsManagementPage />
            </ProtectedRoute>
          } />
          
          <Route path="/hostel/rooms" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <RoomsManagementPage />
            </ProtectedRoute>
          } />
          
          <Route path="/hostel/allocations" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <StudentAllocationsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/hostel/fees" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <HostelFeesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/hostel/attendance" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <HostelAttendancePage />
            </ProtectedRoute>
          } />
          
          <Route path="/hostel/maintenance" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <MaintenanceLogsPageHostel />
            </ProtectedRoute>
          } />
          
          <Route path="/hostel/complaints" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <ComplaintsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/hostel/visitors" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <VisitorLogsPage />
            </ProtectedRoute>
          } />
          
          {/* End Hostel Module Routes */}

          {/* Front Office Module Routes */}
          <Route path="/front-office/visitors" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'security_officer', 'registrar']}>
              <VisitorLogPage />
            </ProtectedRoute>
          } />
          {/* End Front Office Module Routes */}

          {/* Admissions Module Routes */}
          <Route path="/admissions/applicants" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'registrar']}>
              <AdmissionsApplicantsPage />
            </ProtectedRoute>
          } />
          {/* End Admissions Module Routes */}

          {/* Health Module Routes */}
          <Route path="/health/profiles" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'nurse']}>
              <HealthProfilesPage />
            </ProtectedRoute>
          } />
          <Route path="/health/visits" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'nurse']}>
              <ClinicVisitsPage />
            </ProtectedRoute>
          } />
          <Route path="/health/immunizations" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'nurse']}>
              <ImmunizationsPage />
            </ProtectedRoute>
          } />
          <Route path="/health/incidents" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'nurse']}>
              <HealthIncidentsPage />
            </ProtectedRoute>
          } />
          <Route path="/health/screening-campaigns" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'nurse']}>
              <ScreeningCampaignsPage />
            </ProtectedRoute>
          } />
          {/* End Health Module Routes */}

          {/* Discipline Module Routes */}
          <Route path="/discipline/incidents" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'teacher']}>
              <IncidentReportsPage />
            </ProtectedRoute>
          } />
          {/* End Discipline Module Routes */}

          {/* Inventory Module Routes */}
          <Route path="/inventory/categories" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'storekeeper']}>
              <AssetCategoriesPage />
            </ProtectedRoute>
          } />
          <Route path="/inventory/assets" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'storekeeper']}>
              <AssetsRegisterPage />
            </ProtectedRoute>
          } />
          <Route path="/inventory/stock-items" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'storekeeper']}>
              <StockItemsPage />
            </ProtectedRoute>
          } />
          <Route path="/inventory/procurement" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'storekeeper']}>
              <ProcurementPage />
            </ProtectedRoute>
          } />
          <Route path="/hr/performance" element={
            <ProtectedRoute>
              <HRPerformancePage />
            </ProtectedRoute>
          } />
          <Route path="/hr/recruitment" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr']}>
              <HRRecruitmentPage />
            </ProtectedRoute>
          } />
          <Route path="/hr/administration" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr']}>
              <HRAdministrationPage />
            </ProtectedRoute>
          } />
          <Route path="/hr/overtime" element={
            <ProtectedRoute>
              <HROvertimePage />
            </ProtectedRoute>
          } />
          <Route path="/curriculum/planning" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'teacher']}>
              <CurriculumPlanningPage />
            </ProtectedRoute>
          } />
          <Route path="/tracks" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <TracksPage />
            </ProtectedRoute>
          } />
          <Route path="/student-support/secure" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'nurse', 'safeguarding_lead', 'counselor', 'sen_coordinator']}>
              <SensitiveSupportPage />
            </ProtectedRoute>
          } />
          <Route path="/student-support" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'nurse', 'safeguarding_lead']}>
              <StudentSupportPage />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'teacher']}>
              <AnalyticsDashboardPage />
            </ProtectedRoute>
          } />

          <Route path="/strategic-reports" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr']}>
              <StrategicReportsPage />
            </ProtectedRoute>
          } />

          <Route path="/executive-dashboard" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <ExecutiveDashboardPage />
            </ProtectedRoute>
          } />

          <Route path="/executive-insights" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr']}>
              <ExecutiveInsightsPage />
            </ProtectedRoute>
          } />

          <Route path="/budget" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr']} module="finance_advanced">
              <BudgetPage />
            </ProtectedRoute>
          } />

          <Route path="/operational-insights" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr']}>
              <OperationalInsightsPage />
            </ProtectedRoute>
          } />

          <Route path="/strategic-planning" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr']}>
              <StrategicPlanningPage />
            </ProtectedRoute>
          } />
          <Route path="/operations" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'teacher', 'storekeeper']}>
              <OperationsPage />
            </ProtectedRoute>
          } />
          <Route path="/surveys/:surveyId/respond" element={
            <ProtectedRoute>
              <SurveyResponsePage />
            </ProtectedRoute>
          } />
          <Route path="/surveys/new" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'teacher']}>
              <SurveyBuilderPage />
            </ProtectedRoute>
          } />
          {/* End Inventory Module Routes */}

          {/* Alumni Module Routes */}
          <Route path="/alumni/records" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <AlumniRecordsPage />
            </ProtectedRoute>
          } />
          {/* End Alumni Module Routes */}

          {/* Exam Board Module Routes */}
          <Route path="/exam-board/registrations" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <ExamBoardRegistrationsPage />
            </ProtectedRoute>
          } />
          {/* End Exam Board Module Routes */}

          {/* Certificates / ID Cards Module Routes */}
          <Route path="/certificates/issuance" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <CertificateIssuancePage />
            </ProtectedRoute>
          } />
          <Route path="/certificates/templates" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <CertificateTemplatesPage />
            </ProtectedRoute>
          } />
          <Route path="/certificates/id-cards" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr']} module="id_cards">
              <IDCardsPage />
            </ProtectedRoute>
          } />
          {/* End Certificates / ID Cards Module Routes */}

          {/* Campus Module Routes */}
          <Route path="/campuses" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <CampusesPage />
            </ProtectedRoute>
          } />
          {/* End Campus Module Routes */}

          <Route path="/timetable" element={
            <ProtectedRoute module="academic_reports">
              <TimetablePage />
            </ProtectedRoute>
          } />

          <Route path="/exams" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <ExamSchedulingPage />
            </ProtectedRoute>
          } />

          <Route path="/exam-office" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'teacher', 'registrar', 'hr']}>
              <ExamOfficePage />
            </ProtectedRoute>
          } />

          <Route path="/reports" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'hr', 'registrar']}>
              <ReportsPage />
            </ProtectedRoute>
          } />

          <Route path="/announcements" element={
            <ProtectedRoute>
              <AnnouncementsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/tickets" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <TicketsPage />
            </ProtectedRoute>
          } />

          <Route path="/faq" element={
            <ProtectedRoute>
              <FaqPage />
            </ProtectedRoute>
          } />

          <Route path="/parent-requests-admin" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'teacher', 'registrar', 'hr']}>
              <ParentRequestsAdminPage />
            </ProtectedRoute>
          } />

          <Route path="/sms-dashboard" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <SMSDashboardPage />
            </ProtectedRoute>
          } />

          <Route path="/canteen-admin" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'canteen_staff']}>
              <CanteenAdminPage />
            </ProtectedRoute>
          } />

          <Route path="/canteen" element={
            <ProtectedRoute roles={['student']}>
              <CanteenPage />
            </ProtectedRoute>
          } />

          <Route path="/extra-classes-admin" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <ExtraClassesAdminPage />
            </ProtectedRoute>
          } />

          <Route path="/library" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <LibraryAdminPage />
            </ProtectedRoute>
          } />

          <Route path="/settings" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <SettingsPage />
            </ProtectedRoute>
          } />

          <Route path="/settings/roles" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']}>
              <RolesPermissionsPage />
            </ProtectedRoute>
          } />

          <Route path="/settings/integrations" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']} module="integrations">
              <IntegrationsPage />
            </ProtectedRoute>
          } />

          <Route path="/academic-calendar" element={
            <ProtectedRoute roles={['super_admin', 'school_admin']} module="academic_reports">
              <AcademicCalendarPage />
            </ProtectedRoute>
          } />

          <Route path="/parent-portal" element={
            <ProtectedRoute roles={['parent']}>
              <ParentPortalPage />
            </ProtectedRoute>
          } />
          
          <Route path="/student-portal" element={
            <ProtectedRoute roles={['student']}>
              <StudentPortalPage />
            </ProtectedRoute>
          } />
          
          <Route path="/teacher-portal" element={
            <ProtectedRoute roles={['teacher']}>
              <TeacherPortalPage />
            </ProtectedRoute>
          } />
          
          <Route path="/super-admin-portal" element={
            <ProtectedRoute roles={['super_admin']}>
              <SuperAdminPortalPage />
            </ProtectedRoute>
          } />

          <Route path="/hr-portal" element={
            <ProtectedRoute roles={['hr']}>
              <HRPortalPage />
            </ProtectedRoute>
          } />

          <Route path="/nurse-portal" element={
            <ProtectedRoute roles={['nurse']}>
              <NursePortalPage />
            </ProtectedRoute>
          } />

          <Route path="/registrar-portal" element={
            <ProtectedRoute roles={['registrar']}>
              <RegistrarPortalPage />
            </ProtectedRoute>
          } />

          <Route path="/storekeeper-portal" element={
            <ProtectedRoute roles={['storekeeper']}>
              <StorekeeperPortalPage />
            </ProtectedRoute>
          } />

          {/* Student Security Module */}
          <Route path="/security" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'security_officer']}>
              <SecurityDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/security/parent-qr" element={
            <ProtectedRoute roles={['parent']} module="pickup_advanced">
              <ParentQRPage />
            </ProtectedRoute>
          } />
          <Route path="/security/driver" element={
            <ProtectedRoute roles={['driver', 'super_admin', 'school_admin']} module="transport_module">
              <DriverTrackingPage />
            </ProtectedRoute>
          } />
          <Route path="/security/verify" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'security_officer']} module="pickup_advanced">
              <QRVerificationPage />
            </ProtectedRoute>
          } />
          <Route path="/security/admin-tracking" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'security_officer']}>
              <AdminTrackingPage />
            </ProtectedRoute>
          } />
          <Route path="/security/parent-confirm" element={
            <ProtectedRoute roles={['parent']}>
              <ParentSecurityPortalPage />
            </ProtectedRoute>
          } />
          {/* Collector share — token-authenticated, no user login needed */}
          <Route path="/security/collector-share" element={<CollectorSharePage />} />
          <Route path="/security/gate-attendance" element={
            <ProtectedRoute roles={['super_admin', 'school_admin', 'security_officer', 'registrar']} module="gate_attendance">
              <GateAttendancePage />
            </ProtectedRoute>
          } />

          </Route> {/* end AppShell persistent wrapper */}

          {/* Public Landing Page */}
          <Route path="/" element={<LandingPage />} />

          {/* Default Redirect */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
